// Constants and pure helpers shared across the bank surfaces. No JSX, no
// state — anything that needs either lives in the component that owns it.

// Built-in categories carry special behaviour (people: name/role metadata;
// facts: text-only) and are pinned — the manage-categories modal can't rename,
// reorder or delete them. Anything else is a custom image category
// ("clients", "awards" …) created in that modal.
import type { ReactNode } from 'react';
import type { BankItem, BankStatus } from '../../../shared/deck.ts';
import type { ToastApi } from '../../lib/useToast.ts';

/**
 * The toolbar's status tabs. Deliberately wider than `BankStatus`: 'all' is a
 * VIEW over both states, never a state an item can be in.
 */
export type BankStatusFilter = BankStatus | 'all';

/** The confirm dialog a bank surface can raise. */
export type BankConfirm = {
  title: ReactNode;
  body?: ReactNode;
  danger?: boolean;
  confirmLabel?: string;
  onConfirm: () => void;
  /** The non-destructive middle path — "Archive instead" — offered right where
   *  the destructive choice is made, since archiving is what most "remove
   *  this" intentions actually mean. */
  secondaryLabel?: string | null;
  onSecondary?: (() => void) | null;
} | null;

/**
 * What the inline card editor sends.
 *
 * Deliberately NOT a `Partial<BankItem>`: the list fields go out as the
 * COMMA-SEPARATED STRINGS the inputs hold, and the server's `normaliseTagList`
 * splits them (it accepts an array or a string). Typing this as the item shape
 * would have been a lie in the direction that matters — a reader would expect
 * to send arrays.
 */
export type BankItemPatch = {
  label?: string;
  /** departments is a comma-separated string (straight off the input); former
   *  names are an array, because the only writer is the rename undo replaying
   *  an exact prior value rather than a user typing a list. */
  meta?: { name?: string; role?: string; departments?: string; formerNames?: string[] };
  tags?: string;
  category?: string;
};

/**
 * The verb bag every bank view is handed. One type rather than six restatements
 * — BankGrid, BankFacts, BankViews and BankLibrary all destructure from it, and
 * before this each described it only by which keys it happened to pull out.
 */
export type BankActions = {
  /** Runs one mutation behind a busy flag; failures raise a TOAST, not an
   *  alert (the bank is the one surface with no native dialogs left). */
  run: <T>(fn: () => Promise<T>, failPrefix: string) => Promise<T | undefined>;
  reload: () => Promise<void>;
  pushToast: ToastApi['pushToast'];
  setConfirm: (confirm: BankConfirm) => void;
  setItemStatus: (item: BankItem, status: BankStatus) => Promise<void>;
  setMoveItem: (item: BankItem | null) => void;
};

export const BUILTIN_META: Record<string, { label: string; hint: string | null }> = {
  people: { label: 'People', hint: 'Drop a portrait here or click to upload a person' },
  logos: { label: 'Logos', hint: 'Drop a logo here or click to upload' },
  brand: { label: 'Brand imagery', hint: 'Drop brand artwork here or click to upload' },
  facts: { label: 'Text facts', hint: null },
};
export const BUILTIN_CATEGORIES = Object.keys(BUILTIN_META);
export const ACCEPT = '.png,.jpg,.jpeg,.webp,.gif,.svg,image/*';
/** Where "Move the assets instead" files them when a category is deleted. */
export const FALLBACK_CATEGORY = 'brand';

export function labelFor(key: string): string {
  return (
    BUILTIN_META[key]?.label ||
    key
      .split('-')
      .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ')
  );
}

/** The leaf segment of a category path — 'clients/acme' → 'acme'. */
export function leafOf(key: string): string {
  return key.includes('/') ? key.slice(key.indexOf('/') + 1) : key;
}

export function isArchived(item: BankItem): boolean {
  return item.status === 'archived';
}

/** True when `category` is `path` itself or a descendant of it. */
export function isUnder(category: string, path: string): boolean {
  return category === path || category.startsWith(path + '/');
}

/** A row in either bank rail. The tree is two levels deep, so a child's
 *  `children` is always empty — the type is recursive to keep one shape rather
 *  than to promise arbitrary nesting. */
export type BankCategoryNode = {
  path: string;
  label: string;
  count: number;
  children: BankCategoryNode[];
};

/**
 * The two-level category tree both bank rails render: parent rows (built-ins
 * and custom parents) each carrying their sub-categories.
 *
 * Counts are PREFIX-INCLUSIVE, so a parent totals its children — a row that
 * promises more items than the grid it opens reads as a bug. `include` filters
 * which items count (the Library page passes its status filter); pass a
 * constant `true` when every item counts.
 *
 * Shared so the Library's manage rail and the editor's picker rail cannot drift
 * into counting differently, which is the sort of divergence nobody notices
 * until two screens disagree about the same bank.
 */
export function bankCategoryTree(
  categories: string[],
  items: BankItem[],
  include: (item: BankItem) => boolean = () => true,
): BankCategoryNode[] {
  const countOf = (key: string) => items.filter((it) => include(it) && isUnder(it.category, key)).length;
  const parents: string[] = [];
  const seen = new Set<string>();
  for (const k of categories) {
    const p = k.includes('/') ? k.slice(0, k.indexOf('/')) : k;
    if (!seen.has(p)) {
      seen.add(p);
      parents.push(p);
    }
  }
  return parents.map((p) => ({
    path: p,
    label: labelFor(p),
    count: countOf(p),
    // A child row is labelled by its LEAF: under "Clients", the row reads
    // "Acme", not "Clients/acme".
    children: categories
      .filter((k) => k.startsWith(p + '/'))
      .map((k) => ({ path: k, label: labelFor(leafOf(k)), count: countOf(k), children: [] })),
  }));
}

/** Everything a query can match on. Facts match on their body text too — it is
 *  the only place a fact's actual content lives. Former names are in here for
 *  the same reason the resolver honours them: someone searching the maiden name
 *  they still know a colleague by should find her. */
export function matchesQuery(item: BankItem, q: string): boolean {
  const hay = [
    item.label,
    item.id,
    item.meta?.name,
    item.meta?.role,
    ...(item.meta?.formerNames || []),
    item.text,
    ...(item.tags || []),
  ];
  return hay.some((v) => typeof v === 'string' && v.toLowerCase().includes(q));
}

/** Bank category keys are kebab-case paths on disk, not free-text labels, so
 *  anything typed into the shared category modals is normalised here before it
 *  reaches the server ("Clients/Acme" → "clients/acme"). The server validates
 *  the same shape and refuses what this can't fix. */
export function slugifyPath(name: string | null | undefined): string {
  return String(name || '')
    .split('/')
    .map((s) =>
      s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, ''),
    )
    .filter(Boolean)
    .slice(0, 2)
    .join('/');
}
